import React from 'react';
import { motion } from 'framer-motion';
import { PrimaryButton } from './PrimaryButton';
import { RangeSlider } from './RangeSlider';
import { FollicleCluster } from './FollicleCluster';
import { AMH_RANGE, WEIGHT_RANGE, responseTendency, zoneFor, zoneLabels } from '../../utils/responseTendency';
type MiniSimScreenProps = {
  amh: number;
  weight: number;
  onAmh: (v: number) => void;
  onWeight: (v: number) => void;
  onNext: () => void;
};
export function MiniSimScreen({
  amh,
  weight,
  onAmh,
  onWeight,
  onNext
}: MiniSimScreenProps) {
  const position = responseTendency(amh, weight);
  const zone = zoneFor(position);
  return <div className="flex h-full flex-col" data-id="element-143" data-mp="3kx0qfxco522x">
      <h2 className="text-[30px] font-semibold leading-[1.04] tracking-[-0.035em] text-ink" data-id="element-144" data-mp="3px5atygp6gyf">
        Change the patient.
        <br data-id="element-145" data-mp="108watn985nef" />
        See the challenge.
      </h2>

      <div className="mt-5" aria-live="polite" data-id="element-146" data-mp="10ah29408sq82">
        <FollicleCluster position={position} zone={zone} data-id="element-147" data-mp="1564rfjeq5jdk" />
        <div className="relative mt-3 h-4" data-id="element-148" data-mp="at2nr96o66j6">
          <div className="absolute inset-x-0 top-1/2 flex h-[4px] -translate-y-1/2 overflow-hidden rounded-full" data-id="element-149" data-mp="as1cp4pnlk1g">
            <span className="flex-1 bg-ink/10" data-id="element-150" data-mp="6rz9us86mkkl" />
            <span className="w-[32%] bg-accent/70" data-id="element-151" data-mp="1s4hek8rdmxf" />
            <span className="flex-1 bg-ink/10" data-id="element-152" data-mp="1qtesnr1ul7w" />
          </div>
          <motion.span className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-ink shadow-[0_1px_4px_rgba(28,29,34,0.3)]" animate={{
          left: `${position * 100}%`
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-153" data-mp="3szh1b7110eh1" />
        </div>
        <div className="mt-2 grid grid-cols-3 font-mono text-[9px] leading-tight tracking-[0.08em] text-graphite" data-id="element-154" data-mp="3f7sn8y77vfxf">
          <span data-id="element-155" data-mp="3a63ddlvqd8ax">← LOWER</span>
          <span className="text-center text-accent" data-id="element-156" data-mp="358tp49k2v2oo">TARGET ZONE</span>
          <span className="text-right" data-id="element-157" data-mp="35b7hubrac2t2">HIGHER →</span>
        </div>
        <p className={`mt-3 text-center text-[15px] font-semibold tracking-[-0.01em] ${zone === 'target' ? 'text-accent' : 'text-ink'}`} data-id="element-158" data-mp="30bbfegy8kjfm">
          {zoneLabels[zone]}
        </p>
      </div>

      <p className="mt-4 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] text-accent" data-id="element-159" data-mp="2vmiop09g8p46">
        <span className="snak-dot h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" data-id="element-160" data-mp="2hn18h9a7yk6f" />
        DRAG THE SLIDERS
      </p>
      <div className="mt-2 space-y-2" data-id="element-161" data-mp="2lnl2i3zhx7oc">
        <RangeSlider label="AMH" unit="pmol/L" min={AMH_RANGE.min} max={AMH_RANGE.max} value={amh} onChange={onAmh} data-id="element-162" data-mp="39d5m8p3h48yo" />
        <RangeSlider label="BODY WEIGHT" unit="kg" min={WEIGHT_RANGE.min} max={WEIGHT_RANGE.max} value={weight} onChange={onWeight} hintDelay={0.5} data-id="element-163" data-mp="3ec8mzxcf7t0u" />
      </div>
      <p className="mt-2 text-[11px] leading-snug text-graphite" data-id="element-164" data-mp="34kchw2bx32n4">
        AMH drives expected responsiveness most. Body weight shapes the dose, not the response on its own.
      </p>

      <div className="mt-auto pt-4" data-id="element-165" data-mp="yncmixvwlcy">
        <PrimaryButton onClick={onNext} data-id="element-166" data-mp="1lnhot3gblujx">SEE THE REKOVELLE APPROACH</PrimaryButton>
      </div>
    </div>;
}